import { format } from 'date-fns';

const escapeHtml = (value) => {
  if (value === null || value === undefined) return '';
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
};

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === '' || isNaN(Number(value))) return 'N/A';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(Number(value));
};

const formatNumber = (value) => {
  if (value === null || value === undefined || value === '' || isNaN(Number(value))) return 'N/A';
  return new Intl.NumberFormat('en-US').format(Number(value));
};

const normalizeList = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean);
  if (typeof value === 'string') {
    return value
      .split(/\r?\n|\u2028|\u2029|•|-/)
      .map((entry) => entry.trim())
      .filter(Boolean);
  }
  return [];
};

const buildListMarkup = (items) => {
  if (!items.length) return '<p class="muted">Not provided</p>';
  return `<ul>${items.map((item) => `<li>${escapeHtml(item)}</li>`).join('')}</ul>`;
};

const buildKeyValueRow = (label, value) => {
  const safeValue = value ? escapeHtml(value) : '<span class="muted">Not provided</span>';
  return `
    <div class="kv-row">
      <div class="kv-label">${escapeHtml(label)}</div>
      <div class="kv-value">${safeValue}</div>
    </div>
  `;
};

const formatDateTime = (value) => {
  if (!value) return '';
  try {
    return format(new Date(value), 'PPpp');
  } catch (err) {
    return value;
  }
};

const buildCoveragePage = (analysis, projectName) => {
  const report = analysis?.filmCoverageReport || {};
  const metrics = {
    genre: report.genre || 'Not specified',
    ratingEstimate: report.ratingEstimate || 'Not specified',
    marketability: report.marketability || 'Not specified',
    overallEvaluation: report.overallEvaluation || 'No evaluation',
    budgetEstimate: report.budgetEstimate || report.estimatedBudget || 'Not specified',
    tone: report.tone || 'Not specified',
    setting: report.setting || 'Not specified',
  };

  const strengths = buildListMarkup(normalizeList(report.strengths));
  const weaknesses = buildListMarkup(normalizeList(report.weaknesses));
  const recommendations = buildListMarkup(normalizeList(report.recommendations));
  const themes = buildListMarkup(normalizeList(report.themes));

  const targetAudience = report.targetAudience || report.audienceAppeal || 'Not specified';
  const synopsis = report.synopsis || 'Synopsis not available.';
  const logline = report.logline || '';
  const justification = report.justification || report.evaluationJustification || 'No justification provided.';
  const developmentNotes = report.developmentNotes || 'No additional development notes.';

  const created = analysis?.createdAt ? formatDateTime(analysis.createdAt) : '';

  return `
    <div class="page">
      <header class="page-header gradient">
        <div>
          <div class="eyebrow">Film Coverage Report</div>
          <h1>${escapeHtml(projectName || report.title || 'Untitled Screenplay')}</h1>
          ${logline ? `<p class="logline">“${escapeHtml(logline)}”</p>` : ''}
        </div>
        <div class="header-meta">
          ${created ? `<div>${escapeHtml(created)}</div>` : ''}
          <div>Status: <strong>${escapeHtml((analysis?.status || '').replace('_', ' ') || 'Unknown')}</strong></div>
        </div>
      </header>

      <section class="hero-grid">
        <div class="hero-card">
          <div class="hero-label">Synopsis</div>
          <p>${escapeHtml(synopsis)}</p>
        </div>
        <div class="hero-card">
          <div class="hero-label">Key Metrics</div>
          <div class="hero-pill-row">
            <span class="pill">${escapeHtml(metrics.genre)}</span>
            <span class="pill">${escapeHtml(metrics.ratingEstimate)}</span>
            <span class="pill">${escapeHtml(metrics.marketability)}</span>
            <span class="pill">${escapeHtml(metrics.overallEvaluation)}</span>
          </div>
          <small>Target Audience: ${escapeHtml(targetAudience)}</small>
        </div>
      </section>

      <section class="section-grid">
        ${buildKeyValueRow('Genre', metrics.genre)}
        ${buildKeyValueRow('Rating Estimate', metrics.ratingEstimate)}
        ${buildKeyValueRow('Marketability', metrics.marketability)}
        ${buildKeyValueRow('Overall Evaluation', metrics.overallEvaluation)}
        ${buildKeyValueRow('Budget Tier', metrics.budgetEstimate)}
        ${buildKeyValueRow('Tone', metrics.tone)}
        ${buildKeyValueRow('Setting', metrics.setting)}
        ${buildKeyValueRow('Target Audience', targetAudience)}
      </section>

      <section class="columns">
        <div>
          <h3>Strengths</h3>
          ${strengths}
        </div>
        <div>
          <h3>Areas for Improvement</h3>
          ${weaknesses}
        </div>
      </section>

      <section>
        <h3>Recommendations</h3>
        ${recommendations}
      </section>

      ${themes !== '<p class="muted">Not provided</p>' ? `
        <section>
          <h3>Central Themes</h3>
          ${themes}
        </section>
      ` : ''}

      <section>
        <h3>Final Evaluation</h3>
        <div class="evaluation">
          <div class="badge">${escapeHtml(metrics.overallEvaluation)}</div>
          <div>
            <p>${escapeHtml(justification)}</p>
          </div>
        </div>
      </section>

      <section>
        <h3>Development Notes</h3>
        <p>${escapeHtml(developmentNotes)}</p>
      </section>
    </div>
  `;
};

const buildComparablesPage = (analysis, projectName) => {
  const films = Array.isArray(analysis?.comparableFilms)
    ? analysis.comparableFilms
    : [];

  if (!films.length) {
    return `
      <div class="page">
        <header class="page-header">
          <div>
            <div class="eyebrow">Comparable Films</div>
            <h1>${escapeHtml(projectName || 'Comparable Films')}</h1>
          </div>
        </header>
        <div class="placeholder">Comparable film data is not available for this analysis.</div>
      </div>
    `;
  }

  const validFilms = films.filter((film) => {
    const hasFinancialData = ['budget', 'domesticBoxOffice', 'internationalBoxOffice'].some((key) => film[key] && Number(film[key]) > 0);
    return hasFinancialData || film.title;
  });

  const avg = (key) => {
    const entries = validFilms.map((film) => Number(film[key]) || 0).filter((value) => value > 0);
    if (!entries.length) return 'N/A';
    const total = entries.reduce((sum, value) => sum + value, 0) / entries.length;
    return formatCurrency(total);
  };

  const summaryCards = `
    <div class="summary-grid">
      <div class="summary-card">
        <div class="label">Average Budget</div>
        <div class="value">${avg('budget')}</div>
      </div>
      <div class="summary-card">
        <div class="label">Average Domestic</div>
        <div class="value">${avg('domesticBoxOffice')}</div>
      </div>
      <div class="summary-card">
        <div class="label">Average International</div>
        <div class="value">${avg('internationalBoxOffice')}</div>
      </div>
      <div class="summary-card">
        <div class="label">Average Worldwide</div>
        <div class="value">${(() => {
          const dom = validFilms.map((film) => Number(film.domesticBoxOffice) || 0);
          const intl = validFilms.map((film) => Number(film.internationalBoxOffice) || 0);
          if (!dom.length && !intl.length) return 'N/A';
          const total = dom.reduce((sum, value) => sum + value, 0) + intl.reduce((sum, value) => sum + value, 0);
          const count = validFilms.length || 1;
          return formatCurrency(total / count);
        })()}</div>
      </div>
    </div>
  `;

  const rows = films
    .map((film, index) => {
      const worldwide = (Number(film.domesticBoxOffice) || 0) + (Number(film.internationalBoxOffice) || 0);
      const rating = film.rating || film.mpaaRating || 'N/A';
      const score = film.audienceScore ? `${Number(film.audienceScore).toFixed(1)}/10` : 'N/A';
      return `
        <tr>
          <td>${escapeHtml(film.title || `Comparable ${index + 1}`)}</td>
          <td>${film.year || '—'}</td>
          <td>${escapeHtml(film.distributor || '—')}</td>
          <td>${formatCurrency(film.budget)}</td>
          <td>${formatCurrency(film.domesticBoxOffice)}</td>
          <td>${formatCurrency(film.internationalBoxOffice)}</td>
          <td>${formatCurrency(worldwide)}</td>
          <td>${escapeHtml(rating)}</td>
          <td>${escapeHtml(score)}</td>
          <td>${escapeHtml(film.notes || film.miscNotes?.awards || '')}</td>
        </tr>
      `;
    })
    .join('');

  return `
    <div class="page">
      <header class="page-header gradient">
        <div>
          <div class="eyebrow">Comparable Films</div>
          <h1>${escapeHtml(projectName || 'Comparable Films')}</h1>
          <p class="muted">${films.length} market comparables analyzed</p>
        </div>
      </header>

      ${summaryCards}

      <section>
        <table class="data-table">
          <thead>
            <tr>
              <th>Title</th>
              <th>Year</th>
              <th>Distributor</th>
              <th>Budget</th>
              <th>Domestic</th>
              <th>International</th>
              <th>Worldwide</th>
              <th>Rating</th>
              <th>Audience Score</th>
              <th>Notes</th>
            </tr>
          </thead>
          <tbody>
            ${rows}
          </tbody>
        </table>
      </section>
    </div>
  `;
};

const buildEmptyPage = (title, message) => `
  <div class="page">
    <header class="page-header gradient">
      <div>
        <div class="eyebrow">${escapeHtml(title)}</div>
        <h1>${escapeHtml(title)}</h1>
      </div>
    </header>
    <div class="placeholder">${escapeHtml(message)}</div>
  </div>
`;

export const printScreenplayAnalysis = (
  analysis,
  {
    projectName = 'Screenplay Analysis',
    includeCoverage = true,
    includeComparables = true,
  } = {}
) => {
  if (!analysis) {
    console.warn('printScreenplayAnalysis called without analysis data');
    return;
  }

  const pages = [];

  if (includeCoverage) {
    if (analysis?.filmCoverageReport) {
      pages.push(buildCoveragePage(analysis, projectName));
    } else {
      pages.push(buildEmptyPage('Film Coverage Report', 'Coverage data is not available for this analysis.'));
    }
  }

  if (includeComparables) {
    pages.push(buildComparablesPage(analysis, projectName));
  }

  if (!pages.length) {
    pages.push(buildEmptyPage('Screenplay Analysis', 'No printable content selected.'));
  }

  const html = `
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="utf-8" />
        <title>${escapeHtml(projectName)} - Analysis Report</title>
        <style>
          @page {
            size: letter portrait;
            margin: 0.5in;
          }
          body {
            font-family: 'Segoe UI', 'Helvetica Neue', Arial, sans-serif;
            margin: 0;
            padding: 0;
            background: #f5f5f5;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .page {
            background: #fff;
            margin: 0 auto 0.5in;
            padding: 0.75in;
            min-height: 9in;
            box-shadow: 0 8px 24px rgba(0,0,0,0.08);
            box-sizing: border-box;
            page-break-after: always;
            border-radius: 16px;
          }
          .page:last-child { page-break-after: auto; }
          .page-header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            margin-bottom: 1.5rem;
            padding: 1rem;
            border-radius: 14px;
          }
          .page-header.gradient {
            background: linear-gradient(135deg, rgba(98,0,234,0.12), rgba(3,155,229,0.12));
          }
          .eyebrow {
            text-transform: uppercase;
            letter-spacing: 0.15em;
            font-size: 0.75rem;
            color: #9c27b0;
            margin-bottom: 0.35rem;
          }
          h1 {
            margin: 0;
            font-size: 2rem;
            letter-spacing: -0.02em;
          }
          .header-meta {
            text-align: right;
            font-size: 0.9rem;
            color: #616161;
          }
          .logline {
            font-style: italic;
            color: #424242;
            margin-top: 0.5rem;
          }
          section {
            margin-bottom: 1.25rem;
            page-break-inside: avoid;
          }
          h2, h3 {
            margin: 0 0 0.35rem;
          }
          .section-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
            gap: 0.75rem;
          }
          .hero-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
            gap: 1rem;
            margin-bottom: 1.25rem;
          }
          .hero-card {
            background: #faf8ff;
            border: 1px solid rgba(98,0,234,0.15);
            border-radius: 14px;
            padding: 1rem 1.25rem;
          }
          .hero-label {
            font-size: 0.8rem;
            font-weight: 700;
            letter-spacing: 0.12em;
            text-transform: uppercase;
            color: #6a1b9a;
            margin-bottom: 0.5rem;
          }
          .hero-pill-row {
            display: flex;
            flex-wrap: wrap;
            gap: 0.5rem;
            margin-bottom: 0.5rem;
          }
          .pill {
            padding: 0.25rem 0.75rem;
            border-radius: 999px;
            background: #ede7f6;
            font-size: 0.8rem;
            font-weight: 600;
            color: #4527a0;
          }
          .kv-row {
            background: #fafafa;
            border: 1px solid #e0e0e0;
            border-radius: 6px;
            padding: 0.65rem 0.8rem;
          }
          .kv-label {
            font-size: 0.7rem;
            text-transform: uppercase;
            letter-spacing: 0.12em;
            color: #9e9e9e;
          }
          .kv-value {
            font-size: 0.95rem;
            font-weight: 600;
            color: #212121;
          }
          .columns {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
            gap: 1rem;
          }
          ul {
            padding-left: 1.1rem;
            margin: 0;
          }
          li {
            margin-bottom: 0.35rem;
            line-height: 1.4;
          }
          .evaluation {
            display: flex;
            gap: 1rem;
            align-items: center;
          }
          .badge {
            background: #6200ea;
            color: white;
            font-weight: 700;
            padding: 0.35rem 0.9rem;
            border-radius: 999px;
            text-transform: uppercase;
            letter-spacing: 0.08em;
          }
          .summary-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
            gap: 0.75rem;
            margin-bottom: 1rem;
          }
          .summary-card {
            border: 1px solid #e0e0e0;
            border-radius: 12px;
            padding: 0.85rem 1rem;
            background: #fefefe;
            box-shadow: 0 6px 16px rgba(0,0,0,0.05);
          }
          .summary-card .label {
            font-size: 0.8rem;
            color: #757575;
            letter-spacing: 0.08em;
            text-transform: uppercase;
          }
          .summary-card .value {
            font-size: 1.1rem;
            font-weight: 700;
            margin-top: 0.25rem;
          }
          .data-table {
            width: 100%;
            border-collapse: collapse;
            font-size: 0.85rem;
          }
          .data-table th,
          .data-table td {
            border: 1px solid #e0e0e0;
            padding: 0.45rem;
            text-align: left;
          }
          .data-table th {
            background: #f3e5f5;
            text-transform: uppercase;
            font-size: 0.75rem;
            letter-spacing: 0.08em;
          }
          .muted { color: #9e9e9e; }
          .placeholder {
            padding: 1rem;
            border: 1px dashed #bdbdbd;
            border-radius: 8px;
            text-align: center;
            color: #757575;
          }
          @media print {
            body { background: #fff; }
            .page {
              box-shadow: none;
              margin: 0;
            }
          }
        </style>
      </head>
      <body>
        ${pages.join('\n')}
      </body>
    </html>
  `;

  const iframe = document.createElement('iframe');
  iframe.style.display = 'none';
  document.body.appendChild(iframe);

  const doc = iframe.contentDocument || iframe.contentWindow?.document;
  if (!doc) {
    console.error('Unable to access iframe document for printing screenplay analysis');
    document.body.removeChild(iframe);
    return;
  }

  doc.open();
  doc.write(html);
  doc.close();

  iframe.onload = () => {
    setTimeout(() => {
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
      setTimeout(() => document.body.removeChild(iframe), 500);
    }, 300);
  };
};
